import { Icon, Text } from "@ttc3k/trekker";
import { Mail, NavArrowRight } from "iconoir-react";
import { Flex } from "styled-system/jsx";
import { css } from "styled-system/css";
import { useTranslation } from "react-i18next";
import type {
  OperatorTimelineItem,
  RenewalEmailActivityLog,
} from "./useOperatorNotesTimeline";

type SystemEventRowProps = {
  item: OperatorTimelineItem;
  onOpenRenewalEmail?: (log: RenewalEmailActivityLog) => void;
};

export const SystemEventRow = ({
  item,
  onOpenRenewalEmail,
}: SystemEventRowProps) => {
  const { t } = useTranslation("directory", {
    keyPrefix: "OPERATOR.FLYOUT.TIMELINE.SYSTEM",
  });

  const isRenewalEmail =
    item.systemKind === "RENEWAL_EMAIL_SENT" && !!item.renewalEmailLog;

  const label = (() => {
    switch (item.systemKind) {
      case "OPERATOR_CREATED":
        return t("OPERATOR_CREATED");
      case "MEMBERSHIP_STATUS_ADDED":
        return t("MEMBERSHIP_STATUS_ADDED", { name: item.systemLabel });
      case "MEMBERSHIP_STATUS_REMOVED":
        return t("MEMBERSHIP_STATUS_REMOVED", { name: item.systemLabel });
      case "MEMBERSHIP_TIER_ADDED":
        return t("MEMBERSHIP_TIER_ADDED", { name: item.systemLabel });
      case "MEMBERSHIP_TIER_REMOVED":
        return t("MEMBERSHIP_TIER_REMOVED", { name: item.systemLabel });
      case "PAID_UP_DATE_SET":
        return item.systemLabel || t("PAID_UP_DATE_SET");
      case "PAID_UP_DATE_CLEARED":
        return item.systemLabel || t("PAID_UP_DATE_CLEARED");
      case "EMAIL_DECLINED":
        return item.systemLabel || t("EMAIL_DECLINED");
      case "RENEWAL_EMAIL_SENT": {
        const messageStr =
          (item.renewalEmailLog?.message as { en?: string | null } | undefined)
            ?.en ?? t("RENEWAL_EMAIL_SENT");
        return messageStr;
      }
      default:
        return item.systemLabel ?? "";
    }
  })();

  const suffix = item.systemViaSuffix || "";
  const actorName = item.systemActorName;

  if (isRenewalEmail) {
    return (
      <button
        type="button"
        onClick={() =>
          item.renewalEmailLog && onOpenRenewalEmail?.(item.renewalEmailLog)
        }
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "100",
          py: "100",
          px: "150",
          borderRadius: "md",
          border: "1px solid",
          borderColor: "border.dark",
          bg: "white",
          cursor: "pointer",
          textAlign: "left",
          w: "full",
          _hover: { bg: "bg.wa.light" },
        })}
      >
        <Icon Element={Mail} size="sm" color="icon.accent.blue.mid" />
        <Flex flex="1" flexDir="column" gap="25">
          <Text visual="smallRegular" color="text.mid">
            {label}
          </Text>
          {item.systemLabel && (
            <Text visual="smallRegular" color="text.light">
              {item.systemLabel}
            </Text>
          )}
        </Flex>
        <Icon Element={NavArrowRight} size="sm" color="icon.mid" flexShrink={0} ml="100" />
      </button>
    );
  }

  return (
    <Flex
      alignItems="center"
      gap="100"
      py="75"
      px="100"
      borderRadius="sm"
      bg="background.light"
    >
      <Text visual="smallRegular" color="text.mid" flex="1">
        {label}
        {suffix}
      </Text>
      {actorName && (
        <Text visual="smallRegular" color="text.light">
          {actorName}
        </Text>
      )}
    </Flex>
  );
};
